/**
 * El menú del panel: las FASES del libro, en su orden, y debajo las herramientas (Rais, 10-oct-2026: «el menú
 * son en el fondo las fases. La primera es la Biblia. La segunda es hacer el guion. Luego se trabaja el guion,
 * que de hecho es otra cosa. Y luego, diagnóstico y reescritura. Y luego corrección»).
 *
 * Tres filas de tres: las seis fases en las dos primeras; en la tercera, el chat, los ajustes y el tablero de
 * la novela, que no es una pestaña —se abre en el navegador— pero va aquí para que se sepa que existe.
 *
 * Y una cuarta, ancha: el certificado de autoría (Rais, 11-oct-2026: «AWAP y Diagnóstico tendrían que ser dos
 * pastillas diferentes… siempre se puede hacer una más grande»). Antes vivía escondido dentro de Diagnóstico.
 */
export const PESTANAS = [
  ["biblia", "Biblia"], ["guion", "Guion"], ["escritura", "Escritura"],
  ["informes", "Diagnóstico"], ["reescritura", "Reescritura"], ["correccion", "Corrección"],
  ["chat", "Chat"], ["ajustes", "Ajustes"],
  ["awap", "Certificado AWAP"],
] as const;
/** La que va sola en su fila, a lo ancho, debajo de las demás. */
export const PESTANA_ANCHA: (typeof PESTANAS)[number][0] = "awap";
export type Pestana = (typeof PESTANAS)[number][0];
/** Las primeras son las fases del libro; el resto, herramientas. Se pintan distinto. */
export const FASES = 6;
/**
 * Con cuál se abre el panel: donde se trabaja cada día. Si falta lo anterior, esa pestaña manda a hacerlo. Salvo una
 * versión de corrección o de reescritura, que abre en la suya (App, al mirar el documento).
 */
export const PESTANA_DE_ENTRADA: Pestana = "escritura";

/** Qué ha pasado al pedir el tablero: se dice junto al menú, no arriba ni abajo del panel. */
export type EstadoDelTablero = null | { estado: "pidiendo" } | { estado: "abierto"; abierta: boolean; url: string } | { estado: "error"; texto: string };

const celda = { padding: "10px 2px", fontSize: 14, borderRadius: 10, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } as const;

export default function Menu({ tab, alElegir, tablero, alPedirTablero }: { tab: Pestana; alElegir: (p: Pestana) => void; tablero: EstadoDelTablero; alPedirTablero: () => void }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 }}>
        <div role="tablist" aria-label="Las fases del libro y las herramientas" style={{ display: "contents" }}>
          {PESTANAS.filter(([id]) => id !== PESTANA_ANCHA).map(([id, nombre], i) => {
            const activa = tab === id, fase = i < FASES;
            return (
              <button
                key={id} role="tab" aria-selected={activa} onClick={() => alElegir(id)}
                style={{
                  ...celda, fontWeight: activa ? 800 : 700,
                  border: `1px solid ${activa ? "var(--hi-menu-activo)" : "var(--hi-raya)"}`,
                  background: activa ? "var(--hi-menu-activo)" : fase ? "var(--hi-menu)" : "transparent",
                  color: activa ? "var(--hi-sobre-acento)" : fase ? "var(--hi-tinta)" : "var(--hi-suave)",
                }}
              >
                {nombre}
              </button>
            );
          })}
        </div>
        <button
          onClick={alPedirTablero} disabled={tablero?.estado === "pidiendo"}
          title="El tablero de la novela: las escenas de un vistazo, el ritmo, cuánto es tuyo y la conversación con tu coach. Se abre en el navegador de este ordenador; no gasta nada ni toca tu documento."
          style={{ ...celda, fontWeight: 700, border: "1px solid var(--hi-raya)", background: "transparent", color: "var(--hi-suave)" }}
        >
          {tablero?.estado === "pidiendo" ? "Abriendo…" : "Tablero ↗"}
        </button>
        {PESTANAS.filter(([id]) => id === PESTANA_ANCHA).map(([id, nombre]) => {
          const activa = tab === id;
          return (
            <button
              key={id} role="tab" aria-selected={activa} onClick={() => alElegir(id)}
              style={{ ...celda, gridColumn: "1 / -1", fontWeight: activa ? 800 : 700, border: `1px solid ${activa ? "var(--hi-menu-activo)" : "var(--hi-humano-claro)"}`,
                background: activa ? "var(--hi-menu-activo)" : "transparent", color: activa ? "var(--hi-sobre-acento)" : "var(--hi-tinta)" }}
            >
              {nombre}
            </button>
          );
        })}
      </div>
      {tablero?.estado === "abierto" && (tablero.abierta
        ? <div role="status" style={{ fontSize: 12, color: "var(--hi-bien)", marginTop: 6 }}>El tablero de la novela está abierto en el navegador.</div>
        : <div role="status" style={{ fontSize: 12, color: "var(--hi-aviso)", marginTop: 6, overflowWrap: "anywhere" }}>No he podido abrir el navegador. Ábrelo tú y ve a: <span style={{ userSelect: "all" }}>{tablero.url}</span></div>)}
      {tablero?.estado === "error" && <div role="alert" style={{ fontSize: 12, color: "var(--hi-mal)", marginTop: 6 }}>{tablero.texto}</div>}
    </div>
  );
}
